import React from "react";

import { cls } from "app/utils";
import UserAvatar from "app/components/UserAvatar";
import { ageFromBirthdate } from "app/utils/age";

import {
  participantFullName,
  sessionBadgeParticipants,
} from "../helpers/sessionParticipant";
import styles from "./styles.module.scss";

interface Props {
  session: {
    studentProfiles?: any[];
  };
  /** Append "· Age N" when the participant has a birthdate. */
  showAge?: boolean;
  className?: string;
}

/**
 * Pills that name every family member a session is for (CN-1323, CFTB-3279/3280,
 * CFTB-3297). Renders only when the session includes a managed dependent — then
 * names the whole family that attends (the head too, when they're a participant
 * alongside a child), so "me + child" reads differently from "child only". A
 * head-only session has no dependents and renders nothing, so the badge can be
 * dropped onto any session surface without gating on whether the user has a
 * family.
 */
const SessionParticipantBadge = ({ session, showAge = false, className }: Props) => {
  const participants = sessionBadgeParticipants(session);
  if (participants.length === 0) {
    return null;
  }

  return (
    <div className={cls(styles.badges, className)}>
      {participants.map((participant) => {
        const name = participantFullName(participant);
        const age = showAge ? ageFromBirthdate(participant.birthdate) : undefined;

        return (
          <div key={participant.id ?? name} className={styles.badge}>
            <UserAvatar
              sizeWidth={20}
              sizeHeight={20}
              altName={name}
              srcName={participant.profilePicture ?? "default"}
              userName={name}
              className={cls("avatar", styles.badge_avatar)}
            />
            <span className={styles.badge_name}>
              {name}
              {age != null && <span className={styles.badge_age}> · {age}</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default SessionParticipantBadge;
